import { Pause, Play, RotateCcw } from 'lucide-react';
import { useRef, useState } from 'react';
import { useReducedMotion, useVideoKeepAlive } from '../../motion';

const VIDEO_BASE = '/videos/tape/aj-empty-stadium-hero';

export default function TapeRankSting() {
  const videoRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [ended, setEnded] = useState(false);
  const [manualPause, setManualPause] = useState(false);
  const reduced = useReducedMotion();
  useVideoKeepAlive(videoRef, {
    enabled: !reduced && !manualPause && !ended,
    onPlayRejected: () => setPlaying(false),
  });

  const togglePlayback = () => {
    const video = videoRef.current;
    if (!video) return;

    if (ended) {
      video.currentTime = 0;
      setEnded(false);
      setManualPause(false);
      video.play().catch(() => setPlaying(false));
    } else if (video.paused) {
      setManualPause(false);
      video.play().catch(() => setPlaying(false));
    } else {
      setManualPause(true);
      video.pause();
    }
  };

  const buttonLabel = ended ? 'Replay AJ celebration footage' : playing ? 'Pause AJ celebration footage' : 'Play AJ celebration footage';
  const ButtonIcon = ended ? RotateCcw : playing ? Pause : Play;

  return (
    <aside className="mt-6 border border-accent-rust/35 bg-black p-2" data-testid="tape-rank-3-sting">
      <div className="flex flex-wrap items-center justify-between gap-2 px-2 pb-2 pt-1">
        <div className="font-mono text-[9px] font-bold uppercase tracking-[0.22em] text-accent-rust">
          Post-rank footage
        </div>
        <div className="font-display text-[10px] font-black uppercase tracking-[0.16em] text-white/65">
          Third-place parade attendance: 1
        </div>
      </div>

      <div className="relative aspect-video overflow-hidden bg-bg-deep">
        <video
          ref={videoRef}
          className="h-full w-full object-cover"
          muted
          playsInline
          preload="auto"
          poster={`${VIDEO_BASE}-poster.webp`}
          onPlay={() => setPlaying(true)}
          onPause={() => setPlaying(false)}
          onEnded={() => { setPlaying(false); setEnded(true); }}
          aria-label="AJ celebrates alone, then notices the empty stadium"
          data-testid="tape-rank-3-video"
        >
          <source src={`${VIDEO_BASE}-480p.webm`} type="video/webm" />
          <source src={`${VIDEO_BASE}-480p.mp4`} type="video/mp4" />
        </video>

        <div className="pointer-events-none absolute inset-x-0 top-0 flex justify-between bg-gradient-to-b from-black/75 to-transparent px-3 pb-8 pt-3 font-mono text-[8px] font-bold uppercase tracking-[0.18em] text-white/75" aria-hidden="true">
          <span>Highland Park title parade</span>
          <span>Live attendance feed</span>
        </div>

        {ended && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/55 px-5 text-center">
            <div>
              <div className="font-mono text-[8px] font-bold uppercase tracking-[0.22em] text-accent-gold">
                Attendance confirmed
              </div>
              <div className="mt-2 font-display text-2xl font-black uppercase leading-none text-white md:text-3xl">
                Still reasonable at third.
              </div>
            </div>
          </div>
        )}

        <button
          type="button"
          onClick={togglePlayback}
          className="absolute bottom-3 left-1/2 z-10 inline-flex min-h-11 -translate-x-1/2 items-center gap-2 border border-white/40 bg-black/85 px-4 py-2 font-mono text-[10px] font-bold uppercase tracking-[0.14em] text-white transition-colors hover:border-accent-gold hover:text-accent-gold focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent-gold"
          aria-label={buttonLabel}
        >
          <ButtonIcon size={14} aria-hidden="true" />
          {ended ? 'Again' : playing ? 'Hold' : 'Roll'}
        </button>
      </div>

      <p className="px-2 pb-1 pt-3 font-body text-[12px] italic leading-relaxed text-white/70">
        AJ called third reasonable, then held the celebration somewhere between “statement win” and “nobody validated parking.”
      </p>
    </aside>
  );
}
